import { ClientError, GraphQLClient } from 'graphql-request';
import { getSdk, type RefreshTokenMutation, type Sdk } from '@/graphql/generated';
import { tokenStore } from './token-store';

/** Lo que devuelve el servidor al canjear un token de refresco. */
export type SessionPayload = RefreshTokenMutation['refreshToken'];

/**
 * En desarrollo Vite hace de proxy de `/graphql` hacia el BFF, así que el
 * navegador ve un único origen y no hay CORS que negociar.
 *
 * En producción no: la SPA vive en Cloudflare Pages y la API en otra máquina,
 * así que hace falta una URL absoluta y CORS del lado del servidor. Se toma de
 * `VITE_GRAPHQL_URL` en tiempo de compilación; si no está, se cae al origen
 * actual, que es exactamente el comportamiento de desarrollo.
 *
 * La ruta se resuelve siempre a absoluta a propósito: `graphql-request`
 * construye un `URL` con el endpoint y una ruta relativa suelta lo hace
 * reventar con un `TypeError` que, desde fuera, parece un fallo de red.
 */
const ENDPOINT =
  import.meta.env.VITE_GRAPHQL_URL ||
  (typeof window === 'undefined' ? 'http://localhost/graphql' : `${window.location.origin}/graphql`);

/** Operaciones que NO deben pasar por el interceptor de refresco (lo causarían). */
const ANONYMOUS_OPERATIONS = new Set(['Login', 'RefreshToken', 'Logout']);

const rawClient = new GraphQLClient(ENDPOINT, {
  // La sesión viaja en la cabecera `Authorization`, nunca en cookies, así que
  // no hay credenciales que enviar a otro origen. `omit` lo deja explícito y
  // evita que el navegador exija `Access-Control-Allow-Credentials`.
  credentials: 'omit',
});

/** SDK sin interceptor: sólo para login y refresco. */
const bareSdk: Sdk = getSdk(rawClient);

let onSessionExpired: (() => void) | null = null;

/** El proveedor de sesión registra aquí qué hacer cuando el refresco fracasa. */
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

// ── Refresco con cola ───────────────────────────────────────────────────────
// El token de refresco es de UN SOLO USO: el servidor lo rota en cada canje.
// Por eso todo el mundo tiene que pasar por esta única promesa en vuelo:
//
//   · si diez consultas fallan a la vez con 401, sólo una pide token nuevo;
//   · si el arranque de la app y una consulta coinciden (y en desarrollo el
//     StrictMode de React monta los efectos DOS veces), comparten el mismo
//     canje en vez de gastar el token dos veces y desconectar la sesión.

let refreshInFlight: Promise<SessionPayload | null> | null = null;

async function performRefresh(): Promise<SessionPayload | null> {
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) return null;

  try {
    const { refreshToken: payload } = await bareSdk.RefreshToken({ refreshToken });
    tokenStore.setSession(payload.accessToken, payload.refreshToken, payload.expiresIn);
    return payload;
  } catch (error) {
    // Sólo se tira la sesión cuando el servidor RECHAZA el token. Si lo que
    // falló fue la red o el servidor estaba reiniciándose, el token sigue
    // siendo válido: borrarlo obligaría a volver a entrar por un corte de dos
    // segundos, y además perdería el trabajo a medias de la pantalla.
    if (isNetworkError(error)) return null;
    tokenStore.clear();
    onSessionExpired?.();
    return null;
  }
}

/**
 * Canjea el token de refresco, compartiendo la petición con quien ya la esté
 * esperando. Es la única vía admitida para renovar la sesión.
 */
export function refreshSession(): Promise<SessionPayload | null> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function refreshAccessToken(): Promise<string | null> {
  return (await refreshSession())?.accessToken ?? null;
}

// ── Detección de «no autenticado» ───────────────────────────────────────────

/**
 * El BFF puede señalar la falta de sesión de dos formas: con un 401 de HTTP o
 * con un error GraphQL de código `UNAUTHENTICATED` (y HTTP 200, que es lo
 * habitual en GraphQL). Se contemplan las dos.
 */
function isUnauthenticated(error: unknown): boolean {
  if (!(error instanceof ClientError)) return false;
  if (error.response.status === 401) return true;
  return (error.response.errors ?? []).some((e) => {
    const code = e.extensions?.['code'];
    return code === 'UNAUTHENTICATED' || code === 'UNAUTHORIZED';
  });
}

/** `true` si el servidor rechazó por permisos (no por falta de sesión). */
export function isForbiddenError(error: unknown): boolean {
  if (!(error instanceof ClientError)) return false;
  if (error.response.status === 403) return true;
  return (error.response.errors ?? []).some((e) => e.extensions?.['code'] === 'FORBIDDEN');
}

/** Extrae un mensaje presentable en español a partir de cualquier fallo. */
export function errorMessage(error: unknown): string {
  if (error instanceof ClientError) {
    const first = error.response.errors?.[0]?.message;
    if (first) return first;
    if (error.response.status === 0 || error.response.status >= 500) {
      return 'El servidor no respondió. Vuelve a intentarlo en unos segundos.';
    }
    return 'La operación no se pudo completar.';
  }
  if (error instanceof TypeError) {
    // `fetch` lanza TypeError cuando no hay red o el servidor está caído.
    return 'No hay conexión con el servidor. Revisa tu red e inténtalo de nuevo.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Ocurrió un error inesperado.';
}

/** `true` cuando el fallo es de red y no del dominio: merece un reintento. */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (error instanceof ClientError) {
    return error.response.status === 0 || error.response.status >= 500;
  }
  return false;
}

// ── SDK con sesión ──────────────────────────────────────────────────────────

function authHeaders(token: string | null): Record<string, string> {
  return token ? { authorization: `Bearer ${token}` } : {};
}

export const sdk: Sdk = getSdk(rawClient, async (action, operationName) => {
  if (ANONYMOUS_OPERATIONS.has(operationName)) {
    return action();
  }

  // Refresco preventivo: si al token le queda menos de un minuto, se renueva
  // antes de salir, en vez de gastar una petición en descubrir que caducó.
  let token = tokenStore.getAccessToken();
  if (tokenStore.isAccessTokenNearExpiry() && tokenStore.getRefreshToken()) {
    token = (await refreshAccessToken()) ?? token;
  }

  try {
    return await action(authHeaders(token));
  } catch (error) {
    if (!isUnauthenticated(error)) throw error;

    const fresh = await refreshAccessToken();
    // `performRefresh` ya decidió si la sesión estaba caducada de verdad (y en
    // ese caso la limpió y avisó). Aquí sólo queda propagar el fallo original:
    // volver a limpiar cerraría la sesión también cuando el refresco no pudo
    // ni salir a la red.
    if (!fresh) throw error;
    return action(authHeaders(fresh));
  }
});

/** SDK sin autenticación, para el flujo de inicio de sesión. */
export const anonymousSdk = bareSdk;
